import { writable, type Writable, get } from "svelte/store"
import { toasts } from "$lib/stores/toastStores"
import type { ILastWin } from "$lib/interfaces/ILastWin"
import { openDialog } from "$lib/stores/dialogStores"
import { userToken } from "$lib/stores"
import { browser } from "$app/environment"
import { authStore } from "$lib/stores/authStore"

export const isConnected: Writable<boolean> = writable(false)
export const messages: Writable<any[]> = writable([])
export const lastWins: Writable<ILastWin> = writable()

let ws: WebSocket | null = null
const reconnectInterval = 5000 // Time in milliseconds before attempting to reconnect
let reconnectTimeoutId: number | undefined
let reconnectAttempts = 0
const MAX_RECONNECT_ATTEMPTS = 5
let pingInterval: number | undefined
let connectionTimeout: number | undefined
let baseUrl = ""
let isInitialized = false

// Helper function to get the current token from authStore
function getCurrentTokenFromStorage(): string | null {
  if (!browser) return null

  try {
    return authStore.getAccessToken()
  } catch (error) {
    console.error("Error getting token from authStore:", error)
    return null
  }
}

// Helper function to get the WebSocket URL with the current token
function getWebSocketUrlWithCurrentToken(): string {
  // Ensure baseUrl is properly formatted
  let url = baseUrl;

  // Convert http/https to ws/wss if needed
  if (url.startsWith('http://')) {
    url = url.replace('http://', 'ws://');
  } else if (url.startsWith('https://')) {
    url = url.replace('https://', 'wss://');
  }

  // First try to get token from localStorage (most reliable and current source)
  const storageToken = getCurrentTokenFromStorage()

  // If localStorage token exists, use it
  if (storageToken) {
    // Extract the base URL without the token
    const baseUrlWithoutToken = url.split("?")[0]
    return `${baseUrlWithoutToken}?token=${encodeURIComponent(storageToken)}`
  }

  // Fallback to the store token if localStorage token isn't available
  const storeToken = get(userToken)
  if (storeToken) {
    const baseUrlWithoutToken = url.split("?")[0]
    return `${baseUrlWithoutToken}?token=${encodeURIComponent(storeToken)}`
  }

  // If no token is available, return the original URL
  return url
}

export function initWebSocket(url?: string): () => void {
  // Prevent multiple initializations
  if (isInitialized && ws && ws.readyState === WebSocket.OPEN) {
    return () => {}
  }

  isInitialized = true

  if (ws) {
    try {
      ws.close()
    } catch (e) {
      // Ignore errors when closing
    }
    clearInterval(pingInterval)
    clearTimeout(connectionTimeout)
  }

  if (url) {
    baseUrl = url
  }

  if (typeof window === "undefined" || !baseUrl) return () => {}

  try {
    // Always get a fresh WebSocket URL with the current token
    const currentUrl = getWebSocketUrlWithCurrentToken()
    ws = new WebSocket(currentUrl)

    // Add connection timeout
    connectionTimeout = window.setTimeout(() => {
      if (ws && ws.readyState === WebSocket.CONNECTING) {
        // Close the hanging connection attempt
        try {
          ws.close();
        } catch (e) {
          // Ignore errors when closing
        }
        isConnected.set(false);
        attemptReconnect();
      }
    }, 10000); // 10 second timeout

    ws.onopen = (event) => {
      clearTimeout(connectionTimeout);
      isConnected.set(true)
      reconnectAttempts = 0

      if (pingInterval) clearInterval(pingInterval)
      pingInterval = window.setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          try {
            ws.send("ping")
          } catch (e) {
            attemptReconnect()
          }
        }
      }, 20000)
    }

    ws.onmessage = (event) => {
      if (!event.data) return

      if (event.data === "pong" || event.data === "ping") {
        return
      }

      try {
        const data = JSON.parse(event.data)

        switch (data.type) {
          case "BONUS_WAGER_SUCCESS":
            toasts.send(data.content, "success")
            break
          case "LATEST_BETS":
            lastWins.set(data.data)
            break
          case "DEPOSIT":
          case "DEPOSIT_PENDING":
            toasts.send(data.content, "success", 7000, () => {
              const transactionData = {
                orderId: data.data.transactionOrderId || data.data.orderId,
              }
              openDialog("TransactionDetailsModal", transactionData)
            })
            break
          case "REFRESH_NOTIFICATIONS":
            break
        }
      } catch (error) {
        // Silently handle parsing errors
      }
    }

    ws.onerror = (error) => {
      isConnected.set(false)
      attemptReconnect()
    }

    ws.onclose = (event) => {
      isConnected.set(false)
      clearInterval(pingInterval)
      clearTimeout(connectionTimeout)

      if (!event.wasClean) {
        attemptReconnect()
      }
    }
  } catch (error) {
    isConnected.set(false)
    attemptReconnect()
  }

  // Return cleanup function
  return () => {
    if (ws) {
      try {
        ws.close()
      } catch (e) {
        // Ignore errors when closing
      }
      ws = null
    }
    clearInterval(pingInterval)
    clearTimeout(reconnectTimeoutId)
    clearTimeout(connectionTimeout)
    isInitialized = false
  }
}

function attemptReconnect() {
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    return
  }

  clearTimeout(reconnectTimeoutId)
  reconnectTimeoutId = window.setTimeout(() => {
    reconnectAttempts++
    initWebSocket() // This will use the current token from localStorage
  }, reconnectInterval * Math.pow(2, reconnectAttempts)) // Exponential backoff
}

export function sendMessage(message: any): void {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    return
  }

  try {
    if (typeof message === "string" && (message === "ping" || message === "pong")) {
      ws.send(message)
    } else {
      // Otherwise, stringify the message
      ws.send(JSON.stringify(message))
    }
  } catch (error) {
    // Error sending message, attempt reconnect
    attemptReconnect()
  }
}

export function isWebSocketConnected(): boolean {
  return ws?.readyState === WebSocket.OPEN
}

// Helper function to get current WebSocket state
export function getWebSocketState(): string {
  if (!ws) return "CLOSED"

  switch (ws.readyState) {
    case WebSocket.CONNECTING:
      return "CONNECTING"
    case WebSocket.OPEN:
      return "OPEN"
    case WebSocket.CLOSING:
      return "CLOSING"
    case WebSocket.CLOSED:
      return "CLOSED"
    default:
      return "UNKNOWN"
  }
}

// Force reconnection - can be called from components if they detect issues
export function forceReconnect(): void {
  reconnectAttempts = 0
  if (ws) {
    try {
      ws.close()
    } catch (e) {
      // Ignore errors when closing
    }
  }
  initWebSocket() // This will use the current token from localStorage
}
